import React from 'react';
import { useForm } from 'react-hook-form';
import cn from 'clsx';
import styles from './styles.module.scss';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { TextAreaController } from '@components-clean/core/text-area';
import { useTranslation } from '@hooks/index';

interface SubjectNoteReasonForChangeDialogProps {
    isSaving: boolean;
    onCancel: () => void;
    onSubmit: (reasonForChange: string) => Promise<void>;
}

interface ReasonForChangeForm {
    reasonForChangeMessage: string;
}

const SubjectNoteReasonForChangeDialog = ({ isSaving, onCancel, onSubmit }: SubjectNoteReasonForChangeDialogProps) => {
    const { t } = useTranslation('sm', { keyPrefix: 'monitoringToolkit.editSubjectNoteDialog' });
    const { t: tCommon } = useTranslation('common');

    const {
        control,
        handleSubmit,
        formState: { isDirty, isSubmitting, isValid },
    } = useForm<ReasonForChangeForm>({ mode: 'all' });

    return (
        <DraggableDialog
            dataTestId="subject-note-reason-for-change-dialog"
            title={t('reasonForChange')}
            onSubmit={async () =>
                handleSubmit(async (values: ReasonForChangeForm) => {
                    if (values.reasonForChangeMessage.trim().length) {
                        await onSubmit(values.reasonForChangeMessage.trim());
                    }
                })()
            }
            onClose={onCancel}
            primaryBtnLabel={tCommon('save')}
            secondaryBtnLabel={tCommon('cancel')}
            primaryBtnDisabled={!isDirty || !isValid || isSubmitting || isSaving}
            secondaryBtnDisabled={isSubmitting || isSaving}
            initialWidth={552}
            initialHeight={300}
        >
            <div className={cn('k-w-full', 'k-h-full', 'k-d-flex-col')}>
                <TextAreaController
                    control={control}
                    name="reasonForChangeMessage"
                    data-testid="reason-for-change-message"
                    fillMode="solid"
                    maxLength={4000}
                    required
                    readOnly={isSaving}
                    label={`${t('reasonForChange')} *`}
                    className={styles.deleteReason}
                    validateRules={{
                        nonEmpty: (value) => {
                            if (!(value as string).trim().length) {
                                return 'This field is required';
                            }
                            return true;
                        },
                    }}
                />
            </div>
        </DraggableDialog>
    );
};

export default SubjectNoteReasonForChangeDialog;
